<script setup lang="ts">
import { ButtonVariant, Sizes } from '@ui/styles/Styles'
import Times from '~icons/cue-regular/times'
import Backdrop from '../backdrop/Backdrop.vue'
import Button from '../button/Button.vue'
import IconButton from '../icon/IconButton.vue'

type PopoverActionsAlign = 'stretch' | 'end'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    closable?: boolean
    divider?: boolean
    cancelLabel?: string
    confirmLabel?: string
    confirmDisabled?: boolean
    size?: string
    titleClass?: string
    loading?: boolean
    padding?: boolean
    actionsAlign?: PopoverActionsAlign
    actionsReverse?: boolean
  }>(),
  {
    title: '',
    description: '',
    closable: true,
    divider: true,
    cancelLabel: '',
    confirmLabel: '',
    confirmDisabled: false,
    size: '328px',
    titleClass: '',
    loading: false,
    padding: true,
    actionsAlign: 'stretch',
    actionsReverse: false,
  },
)

const emit = defineEmits<{
  cancel: []
  confirm: []
}>()

defineSlots<{
  default?: () => void
  description?: () => void
}>()

const show = defineModel<boolean>('show', { default: false })

const footerClass = computed(() => ({
  _divided: props.divider,
  _end: props.actionsAlign === 'end',
  _reverse: props.actionsReverse,
}))

const buttonClass = computed(() => ({
  'flex-1': props.actionsAlign === 'stretch',
}))

function onCancel() {
  emit('cancel')
  show.value = false
}
</script>

<template lang="pug">
Backdrop(v-model:show="show" :zIndex="800")
  .popover.br-24.p-20-0.flex.col-0.overflow-hidden(role="dialog" v-bind="$attrs" :style="{ width: size }")
    .popover__header.flex.col-4.p-0-20-16
      IconButton.m-l-auto(
        v-if="closable"
        :name="Times"
        :variant="ButtonVariant.Function"
        :size="Sizes.Luke"
        @click="show = false"
      )
      .popover__header-content.flex.col-12.flex-1
        .popover__title.typ-18-26-600(:class="titleClass") {{ $t(title) }}
        .popover__description.typ-16-24-400(v-if="description || $slots.description")
          slot(name="description") {{ $t(description) }}
    .popover__body.p-x-20(v-if="$slots.default" :class="padding ? 'p-b-16' : ''")
      slot
    .popover__footer.flex.row-16.p-20-20-0(
      v-if="cancelLabel || confirmLabel"
      :class="footerClass"
    )
      Button(
        v-if="cancelLabel"
        :class="buttonClass"
        :label="cancelLabel"
        :variant="ButtonVariant.Secondary"
        :size="Sizes.HanSolo"
        :loading="loading"
        @click="onCancel"
      )
      Button(
        v-if="confirmLabel"
        :class="buttonClass"
        :label="confirmLabel"
        :variant="ButtonVariant.Primary"
        :size="Sizes.HanSolo"
        :loading="loading"
        :disabled="confirmDisabled"
        @click="$emit('confirm')"
      )
</template>

<style scoped>
.popover {
  background: var(--cue-bg-surface-primary);
  box-shadow: 0 4px 24px 0 color-mix(in srgb, var(--cue-static-neutral-inverse) 12%, transparent);
  max-width: calc(100vw - 32px);
}

.popover__title {
  color: var(--cue-content-neutral-primary);
}

.popover__description {
  color: var(--cue-content-neutral-secondary);
}

.popover__footer {
  &._end {
    justify-content: flex-end;
  }

  &._reverse {
    flex-flow: row-reverse;
  }

  &._divided {
    border-top: 1px solid var(--cue-border-neutral-primary);
  }
}
</style>
